import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Typography, Button, Modal, message } from "antd";
import { useQueryClient } from "@tanstack/react-query";
import {
  CheckOutlined,
  RocketOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import { useAuth } from "../context/AuthContext";
import {
  PLAN_DETAILS,
  PLAN_ORDER,
  PLANS,
  normalizePlan,
} from "../constants/plans";
import { profileKeys } from "../api/profileApi";
import {
  useCreateCheckout,
  useSubscription,
  useCancelSubscription,
} from "../api/billingApi";

const { Title, Text } = Typography;

// Only these plans have a real Lemon Squeezy checkout; Free is a no-cost
// switch and Professional is handled via a sales quote, not self-serve.
const CHECKOUT_PLANS = [PLANS.STANDARD, PLANS.PLUS];

export const Plans = () => {
  const { currentUser, refreshMe } = useAuth();
  const activePlan = normalizePlan(currentUser?.plan);
  const isProfessional = activePlan === PLANS.PROFESSIONAL;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const checkout = useCreateCheckout();
  const { data: subscription } = useSubscription();
  const cancelSubscription = useCancelSubscription();

  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  const scheduledDate = subscription?.scheduledChangeAt
    ? new Date(subscription.scheduledChangeAt).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    })
    : null;

  // Cancellation has a grace period — the subscription stays `cancelled`
  // (not yet Free) until Lemon Squeezy's `ends_at` date passes.
  const isCancelled = subscription?.status === "cancelled";
  const endsAtDate = subscription?.endsAt
    ? new Date(subscription.endsAt).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    })
    : null;

  const goToProfile = () => {
    navigate("/profile");
  };

  const handleUpgrade = (planKey) => {
    if (!CHECKOUT_PLANS.includes(planKey)) return;
    checkout.mutate(planKey, {
      onSuccess: (data) => {
        // Downgrades between paid tiers are deferred to the end of the
        // current billing period rather than applied right away, so the
        // user keeps what they already paid for until then.
        if (data?.scheduled) {
          const date = data.effectiveAt
            ? new Date(data.effectiveAt).toLocaleDateString("en-US", {
              month: "long",
              day: "numeric",
              year: "numeric",
            })
            : "your next billing date";
          message.info(
            `You'll switch to the ${PLAN_DETAILS[planKey].name} plan on ${date}.`,
          );
          queryClient.invalidateQueries({
            queryKey: ["billing", "subscription"],
          });
          return;
        }
        // Existing subscribers get their plan changed in place (no checkout
        // redirect) — the backend updates Lemon Squeezy directly and the
        // webhook then syncs our own DB, so just refresh and let the user
        // know.
        if (data?.updated) {
          message.success(
            `Switched to the ${PLAN_DETAILS[planKey].name} plan.`,
          );
          refreshMe().catch(() => { });
          queryClient.invalidateQueries({ queryKey: profileKeys.details() });
          queryClient.invalidateQueries({
            queryKey: ["billing", "subscription"],
          });
        }
      },
      onError: (error) => {
        message.error(
          error?.response?.data?.error ||
          "Unable to start checkout. Please try again.",
        );
      },
    });
  };

  // Opens the declarative cancel-confirmation modal (styled to match the
  // session-name / rename modals in CurrentSessionCard) instead of using
  // the imperative Modal.confirm() API.
  const openCancelModal = () => setIsCancelModalOpen(true);
  const closeCancelModal = () => setIsCancelModalOpen(false);

  const confirmCancelToFree = () => {
    cancelSubscription.mutate(undefined, {
      onSuccess: () => {
        message.success(
          "Your subscription is cancelled. You'll keep access until it ends.",
        );
        closeCancelModal();
      },
      onError: (error) => {
        message.error(
          error?.response?.data?.error ||
          "Unable to cancel your subscription. Please try again.",
        );
      },
    });
  };

  const getCtaLabel = (planKey) => {
    const plan = PLAN_DETAILS[planKey];
    if (!CHECKOUT_PLANS.includes(planKey)) return plan.cta;
    const isDowngrade =
      PLAN_ORDER.indexOf(planKey) < PLAN_ORDER.indexOf(activePlan);
    return isDowngrade ? `Switch to ${plan.name}` : plan.cta;
  };

  return (
    <div className="w-full flex items-center justify-center pt-24">
      <div className="pricing-shell flex flex-col items-center gap-8 p-8">
        <div className="text-center max-w-xl">
          <Title level={2} className="display" style={{ margin: 0 }}>
            Plans & pricing
          </Title>
        </div>

        {/* Plan grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5 max-w-6xl w-full">
          {PLAN_ORDER.map((planKey) => {
            const plan = PLAN_DETAILS[planKey];
            const isCurrent = planKey === activePlan;
            const isScheduled = subscription?.scheduledPlan === planKey;
            return (
              <div
                key={planKey}
                className={`relative flex flex-col bg-white rounded-2xl border shadow-sm p-6 ${isCurrent
                    ? "border-violet-500 shadow-violet-100 shadow-md"
                    : "border-gray-200"
                  }`}
              >
                {isCurrent && (
                  <span className="absolute -top-3 left-6 bg-violet-600 text-white text-xs font-medium px-3 py-1 rounded-full">
                    Your plan
                  </span>
                )}

                <Title level={4} className="display" style={{ margin: 0 }}>
                  {plan.name}
                </Title>

                <div className="mt-4 mb-1">
                  <span className="display text-3xl font-semibold">
                    {plan.isCustomPrice
                      ? "Custom"
                      : plan.price === 0
                        ? "$0"
                        : `$${plan.price}`}
                  </span>
                  {!plan.isCustomPrice && plan.price !== 0 && (
                    <Text type="secondary" className="ml-1.5 text-sm">
                      / mo
                    </Text>
                  )}
                </div>

                <ul className="mt-4 mb-6 space-y-2 flex-1">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <CheckOutlined
                        className="text-violet-600 mt-1"
                        style={{ fontSize: 11 }}
                      />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  type={isCurrent ? "default" : "primary"}
                  danger={planKey === PLANS.FREE && !isCurrent && !isCancelled}
                  icon={
                    !isCurrent && !isScheduled && planKey !== PLANS.FREE ? (
                      <RocketOutlined />
                    ) : undefined
                  }
                  disabled={
                    isCurrent ||
                    isScheduled ||
                    (planKey === PLANS.FREE && isCancelled) ||
                    (isProfessional && !isCurrent)
                  }
                  loading={
                    (checkout.isPending && checkout.variables === planKey) ||
                    (planKey === PLANS.FREE && cancelSubscription.isPending)
                  }
                  onClick={() => {
                    if (planKey === PLANS.FREE) {
                      openCancelModal();
                    } else if (planKey === PLANS.PROFESSIONAL) {
                      window.location.href =
                        "mailto:admin@dittrium.com?subject=Professional%20Plan%20Inquiry";
                    } else {
                      handleUpgrade(planKey);
                    }
                  }}
                  className={
                    isCurrent || isScheduled || planKey === PLANS.FREE
                      ? "rounded-lg h-10"
                      : "bg-violet-600 hover:!bg-violet-500 border-violet-600 rounded-lg h-10"
                  }
                >
                  {isCurrent
                    ? "Current plan"
                    : isScheduled
                      ? `Scheduled for ${scheduledDate}`
                      : planKey === PLANS.FREE && isCancelled
                        ? `Ends ${endsAtDate}`
                        : isProfessional
                          ? "Unavailable"
                          : getCtaLabel(planKey)}
                </Button>
              </div>
            );
          })}
        </div>

        <Button
          ghost
          type="primary"
          icon={<ArrowLeftOutlined />}
          onClick={goToProfile}
          size="middle"
        >
          Back to profile
        </Button>
      </div>

      <Modal
        title={<span className="text-gray-900">Cancel your subscription?</span>}
        open={isCancelModalOpen}
        onOk={confirmCancelToFree}
        onCancel={closeCancelModal}
        confirmLoading={cancelSubscription.isPending}
        centered
        okText="Cancel subscription"
        cancelText="Keep my plan"
        okButtonProps={{ danger: true, style: { color: "#ffffff" } }}
        cancelButtonProps={{ type: "primary", ghost: true }}
      >
        <p className="text-gray-700">
          {subscription?.renewsAt
            ? `You'll keep your ${PLAN_DETAILS[activePlan].name} plan until ${new Date(
              subscription.renewsAt,
            ).toLocaleDateString("en-US", {
              month: "long",
              day: "numeric",
              year: "numeric",
            })}, then move to Free.`
            : "You'll keep your current plan until the end of the billing period, then move to Free."}
        </p>
      </Modal>
    </div>
  );
};
